import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Product } from 'src/app/core/types/product.type';
import { lastValueFrom, take } from 'rxjs';
import Order from 'src/app/core/models/order';
import { OrderStatus } from 'src/app/core/enums/orderStatus.enum';
import { OrderCreationError } from 'src/app/core/enums/orderCreationError.enum';
import { CheckoutSessionCode } from 'src/app/core/enums/checkout-session-code.enum';

export type CheckoutSessionServiceBaseResponse<D, T> = {
  code: number;
  details: D;
  data: T;
};

export type CheckoutSessionStartResponse = CheckoutSessionServiceBaseResponse<
  { ticket_types_ids: number[] },
  { session_token: string; in_session_count_events: string[] }
>;

export type NonEmptyDiscountTokens = [string, ...string[]];

export type CheckoutSessionDto = {
  session_token: string;
  buyer_email?: string;
  buyer_phone?: string;
  buyer_full_name: string;
  google_pay_token?: string;
  apple_pay_token?: string;
  promoter_slug?: string;
  is_secured: boolean;
  discount_tokens?: NonEmptyDiscountTokens;
  recaptchaToken?: string;
  // Only sent on the retry, once the backend has asked for a challenge.
  challengeToken?: string;
  card_info: {
    buyer_full_name: string;
    cvc: string;
    exp: string;
    card: string;
    type?: string;
  };
};

export type CheckoutAzulSuccessResponse = {
  paymentId: number;
  TermUrl: string;
  MethodNotificationUrl: string;
  AuthorizationCode: string;
  ResponseMessage: string;
  ResponseCode: string;
  ThreeDSMethod: { MethodForm: string };
  ThreeDSChallenge: {
    CReq: string;
    MD: string;
    PaReq: string;
    RedirectPostUrl: string;
  };
  Ticket: string;
};

export type CheckoutAzulErrorResponse = {
  auth_code: string;
  response_code: string;
  response_message: string;
  response_error: string;
  iso_code: string;
};

export type CheckoutTixErrorResponse = {
  code: string;
  error: string;
  msg: string;
  iso_code: string;
  tix_error: OrderCreationError;
};

export type CheckoutAzulResponse =
  | CheckoutAzulErrorResponse
  | CheckoutAzulSuccessResponse;

export type CheckoutSessionErrorResponse = {
  code: CheckoutSessionCode;
};

export type OrderCreatedResponse = {
  code: number;
  details: {
    ticket_types_ids: number[];
  };
  data: {
    pixel_id?: number;
    gtm_id?: string;
    order: {
      id: number;
      buyer_full_name: string;
      buyer_email: string;
      status: OrderStatus;
      slug: string;
      total: number;
      sub_total: number;
      service_fee: number;
      insurance_fee: number;
      created_at: string;
      tickets: [{ id: number; status: string }];
    };
  };
};

export type CheckoutResponse =
  | CheckoutAzulResponse
  | OrderCreatedResponse
  | CheckoutTixErrorResponse;

@Injectable({
  providedIn: 'root',
})
export class CheckoutSessionService {
  constructor(private http: HttpClient) {}

  validateMerchant() {
    const url = `${environment.apiUrl}/checkout/validateMerchant`;
    const req = this.http.get(url).pipe(take(1));
    return lastValueFrom(req);
  }

  async startSession(
    eventId: number,
    products: Product[],
    email?: string
  ): Promise<CheckoutSessionStartResponse> {
    const req = this.http
      .post<CheckoutSessionStartResponse>(`${environment.apiUrl}/checkout`, {
        products,
        event_id: eventId,
        email,
      })
      .pipe(take(1));
    return lastValueFrom<CheckoutSessionStartResponse>(req);
  }

  async endSession(sessionToken: string, expired = false): Promise<void> {
    const req = this.http
      .post(`${environment.apiUrl}/checkout/end`, {
        session_token: sessionToken,
        expired,
      })
      .pipe(take(1));
    await lastValueFrom(req);
  }

  async checkout(dto: CheckoutSessionDto): Promise<CheckoutResponse> {
    const req = this.http
      .post<CheckoutAzulResponse>(`${environment.apiUrl}/orders`, {
        session_token: dto.session_token,
        google_pay_token: dto.google_pay_token,
        apple_pay_token: dto.apple_pay_token,
        discount_tokens: dto.discount_tokens,
        buyer_email: dto.buyer_email,
        buyer_phone: dto.buyer_phone,
        buyer_full_name: dto.buyer_full_name,
        promoter_slug: dto.promoter_slug,
        is_secured: dto.is_secured,
        recaptchaToken: dto.recaptchaToken,
        challengeToken: dto.challengeToken,
        card_info: dto.card_info,
      })
      .pipe(take(1));
    return await lastValueFrom(req);
  }
}
